import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo-experimental/experience-types';
import { payloadPublisher } from '@atlassian/ufo/publisher';
import { UFOExperience } from '@atlassian/ufo-experimental/experience';
import type { ScriptResourceTimingPhases } from '../../utils/performance';
import {
	isBubbleImageLoadComplete,
	isBubbleImageLoadPending,
} from '../../utils/bubble-image-load-times';
import { client } from './analytics-client';

export const panelLoadExperience: UFOExperience = new UFOExperience('csm-ai-widget-iframe-load', {
	type: ExperienceTypes.Load,
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
});

export const widgetOpenToOnlineExperience: UFOExperience = new UFOExperience(
	'csm-ai-widget-open-to-online',
	{
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	},
);

export enum WidgetLoadExperienceSegments {
	/**
	 * The time it takes for the entrypoint script to load
	 */
	SCRIPT_LOAD = 'script-load',
	/**
	 * Time the browser spent stalled before DNS, connect, or the request started
	 */
	SCRIPT_STALLED = 'script-stalled',
	/**
	 * DNS lookup for the entrypoint script
	 */
	SCRIPT_DNS = 'script-dns',
	/**
	 * TCP + TLS connection setup for the entrypoint script
	 */
	SCRIPT_CONNECT = 'script-connect',
	/**
	 * Time to first byte (request sent until first response byte) for the entrypoint script
	 */
	SCRIPT_WAIT = 'script-wait',
	/**
	 * Content download of the entrypoint script
	 */
	SCRIPT_DOWNLOAD = 'script-download',
	/**
	 * The time from when the script has loaded until the SDK is initialized
	 */
	SDK_INIT = 'sdk-init',
	/**
	 * The time it takes to fetch the widget configuration from the server
	 */
	CONFIG_FETCH = 'config-fetch',
	/**
	 * The time it takes for the customer authenticate callback to complete
	 */
	AUTHENTICATE = 'authenticate',
	/**
	 * The time it takes to perform the access check
	 */
	ACCESS_CHECK = 'access-check',
	/**
	 * The time from when the script has loaded the SDK until the widget has initialized
	 * (rendered the bubble in the DOM, iframe not yet loaded)
	 */
	WIDGET_INIT = 'widget-init',
	/**
	 * The time to load the iframe from when the iframe HTML starts to load
	 * until the iframe has loaded its HTML
	 */
	IFRAME_HTML_LOAD = 'iframe-html-load',
	/**
	 * The time to load the iframe from when the iframe HTML finishes loading
	 * until the iframe has loaded the JS and has evaluated it
	 */
	IFRAME_JS_LOAD = 'iframe-js-load',
	/**
	 * The time from when the iframe HTML has loaded until the parent host
	 * receives the init message
	 */
	IFRAME_INIT = 'iframe-init',
	/**
	 * The time from when the component goes from launching to online
	 */
	COMPONENT_LAUNCH = 'component-launch',
	/**
	 * The time it takes for the launcher tile image to load or fail
	 * so the bubble is visually ready
	 */
	BUBBLE_IMAGE_LOAD = 'bubble-image-load',
}

export enum WidgetLoadExperienceMarks {
	SCRIPT_LOAD_START = 'script-load-start',
	SCRIPT_LOAD_END = 'script-load-end',
	SCRIPT_STALLED_START = 'script-stalled-start',
	SCRIPT_STALLED_END = 'script-stalled-end',
	SCRIPT_DNS_START = 'script-dns-start',
	SCRIPT_DNS_END = 'script-dns-end',
	SCRIPT_CONNECT_START = 'script-connect-start',
	SCRIPT_CONNECT_END = 'script-connect-end',
	SCRIPT_WAIT_START = 'script-wait-start',
	SCRIPT_WAIT_END = 'script-wait-end',
	SCRIPT_DOWNLOAD_START = 'script-download-start',
	SCRIPT_DOWNLOAD_END = 'script-download-end',
	SDK_INIT = 'sdk-init',
	CONFIG_FETCH_START = 'config-fetch-start',
	CONFIG_FETCH_END = 'config-fetch-end',
	AUTHENTICATE_START = 'authenticate-start',
	AUTHENTICATE_END = 'authenticate-end',
	ACCESS_CHECK_START = 'access-check-start',
	ACCESS_CHECK_END = 'access-check-end',
	WIDGET_INIT = 'widget-init',
	IFRAME_HTML_LOAD_START = 'iframe-html-load-start',
	IFRAME_HTML_LOAD_END = 'iframe-html-load-end',
	IFRAME_LOAD_END = 'iframe-load-end',
	COMPONENT_LAUNCHING = 'component-launching',
	COMPONENT_ONLINE = 'component-online',
	BUBBLE_IMAGE_LOAD_START = 'bubble-image-load-start',
	BUBBLE_IMAGE_LOAD_END = 'bubble-image-load-end',
}

export const widgetLoadExperience: UFOExperience = new UFOExperience('csm-ai-widget-load', {
	type: ExperienceTypes.Load,
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	timings: [
		{
			key: WidgetLoadExperienceSegments.SCRIPT_LOAD,
			startMark: WidgetLoadExperienceMarks.SCRIPT_LOAD_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_LOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.SCRIPT_STALLED,
			startMark: WidgetLoadExperienceMarks.SCRIPT_STALLED_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_STALLED_END,
		},
		{
			key: WidgetLoadExperienceSegments.SCRIPT_DNS,
			startMark: WidgetLoadExperienceMarks.SCRIPT_DNS_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_DNS_END,
		},
		{
			key: WidgetLoadExperienceSegments.SCRIPT_CONNECT,
			startMark: WidgetLoadExperienceMarks.SCRIPT_CONNECT_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_CONNECT_END,
		},
		{
			key: WidgetLoadExperienceSegments.SCRIPT_WAIT,
			startMark: WidgetLoadExperienceMarks.SCRIPT_WAIT_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_WAIT_END,
		},
		{
			key: WidgetLoadExperienceSegments.SCRIPT_DOWNLOAD,
			startMark: WidgetLoadExperienceMarks.SCRIPT_DOWNLOAD_START,
			endMark: WidgetLoadExperienceMarks.SCRIPT_DOWNLOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.SDK_INIT,
			startMark: WidgetLoadExperienceMarks.SCRIPT_LOAD_END,
			endMark: WidgetLoadExperienceMarks.SDK_INIT,
		},
		{
			key: WidgetLoadExperienceSegments.CONFIG_FETCH,
			startMark: WidgetLoadExperienceMarks.CONFIG_FETCH_START,
			endMark: WidgetLoadExperienceMarks.CONFIG_FETCH_END,
		},
		{
			key: WidgetLoadExperienceSegments.AUTHENTICATE,
			startMark: WidgetLoadExperienceMarks.AUTHENTICATE_START,
			endMark: WidgetLoadExperienceMarks.AUTHENTICATE_END,
		},
		{
			key: WidgetLoadExperienceSegments.ACCESS_CHECK,
			startMark: WidgetLoadExperienceMarks.ACCESS_CHECK_START,
			endMark: WidgetLoadExperienceMarks.ACCESS_CHECK_END,
		},
		{
			key: WidgetLoadExperienceSegments.WIDGET_INIT,
			startMark: WidgetLoadExperienceMarks.SDK_INIT,
			endMark: WidgetLoadExperienceMarks.WIDGET_INIT,
		},
		{
			key: WidgetLoadExperienceSegments.IFRAME_HTML_LOAD,
			startMark: WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_START,
			endMark: WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.IFRAME_JS_LOAD,
			startMark: WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_END,
			endMark: WidgetLoadExperienceMarks.IFRAME_LOAD_END,
		},
		{
			key: WidgetLoadExperienceSegments.COMPONENT_LAUNCH,
			startMark: WidgetLoadExperienceMarks.COMPONENT_LAUNCHING,
			endMark: WidgetLoadExperienceMarks.COMPONENT_ONLINE,
		},
		{
			key: WidgetLoadExperienceSegments.BUBBLE_IMAGE_LOAD,
			startMark: WidgetLoadExperienceMarks.BUBBLE_IMAGE_LOAD_START,
			endMark: WidgetLoadExperienceMarks.BUBBLE_IMAGE_LOAD_END,
		},
	],
});

export type WidgetLoadHostMetrics = ScriptResourceTimingPhases & {
	scriptLoadStartTime?: number;
	scriptLoadEndTime?: number;
	sdkInitTime: number;
	instanceInitTime: number;
	shellMountTime?: number;
	iframeInitTime: number;
	iframeHTMLLoadStartTime?: number;
	iframeHTMLLoadEndTime?: number;
	iframeLoadEndTime: number;
	configFetchStartTime: number;
	configFetchEndTime: number;
	authenticateStartTime: number;
	authenticateEndTime: number;
	accessCheckStartTime: number;
	accessCheckEndTime: number;
	bubbleImageLoadStartTime?: number;
	bubbleImageLoadEndTime?: number;
};

const getHostSiteMarkForMetrics = (
	metrics: Pick<WidgetLoadHostMetrics, 'iframeInitTime' | 'scriptLoadStartTime'>,
	hostMetricTimestamp: number,
): number => {
	const originOffset = -1 * (metrics.iframeInitTime - (metrics.scriptLoadStartTime ?? 0));
	return originOffset + hostMetricTimestamp - (metrics.scriptLoadStartTime ?? 0);
};

export const markWidgetLoadStart = (metrics: WidgetLoadHostMetrics): void => {
	try {
		const originOffset = -1 * (metrics.iframeInitTime - (metrics.scriptLoadStartTime ?? 0));
		widgetLoadExperience.start(originOffset);

		/**
		 * Computes a negative mark for the events that happened before the timeorigin in the iframe
		 */
		const getHostSiteMark = (hostMetricTimestamp: number | undefined) =>
			originOffset + (hostMetricTimestamp ?? 0) - (metrics?.scriptLoadStartTime ?? 0);

		const markPhase = (
			startMark: WidgetLoadExperienceMarks,
			endMark: WidgetLoadExperienceMarks,
			startTime?: number,
			endTime?: number,
		) => {
			if (startTime === undefined || endTime === undefined) {
				return;
			}
			widgetLoadExperience.mark(startMark, getHostSiteMark(startTime));
			widgetLoadExperience.mark(endMark, getHostSiteMark(endTime));
		};

		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.SCRIPT_LOAD_START,
			getHostSiteMark(metrics.scriptLoadStartTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.SCRIPT_LOAD_END,
			getHostSiteMark(metrics.scriptLoadEndTime),
		);
		markPhase(
			WidgetLoadExperienceMarks.SCRIPT_STALLED_START,
			WidgetLoadExperienceMarks.SCRIPT_STALLED_END,
			metrics.scriptStalledStartTime,
			metrics.scriptStalledEndTime,
		);
		markPhase(
			WidgetLoadExperienceMarks.SCRIPT_DNS_START,
			WidgetLoadExperienceMarks.SCRIPT_DNS_END,
			metrics.scriptDnsStartTime,
			metrics.scriptDnsEndTime,
		);
		markPhase(
			WidgetLoadExperienceMarks.SCRIPT_CONNECT_START,
			WidgetLoadExperienceMarks.SCRIPT_CONNECT_END,
			metrics.scriptConnectStartTime,
			metrics.scriptConnectEndTime,
		);
		markPhase(
			WidgetLoadExperienceMarks.SCRIPT_WAIT_START,
			WidgetLoadExperienceMarks.SCRIPT_WAIT_END,
			metrics.scriptWaitStartTime,
			metrics.scriptWaitEndTime,
		);
		markPhase(
			WidgetLoadExperienceMarks.SCRIPT_DOWNLOAD_START,
			WidgetLoadExperienceMarks.SCRIPT_DOWNLOAD_END,
			metrics.scriptDownloadStartTime,
			metrics.scriptDownloadEndTime,
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.SDK_INIT,
			getHostSiteMark(metrics.sdkInitTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.CONFIG_FETCH_START,
			getHostSiteMark(metrics.configFetchStartTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.CONFIG_FETCH_END,
			getHostSiteMark(metrics.configFetchEndTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.AUTHENTICATE_START,
			getHostSiteMark(metrics.authenticateStartTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.AUTHENTICATE_END,
			getHostSiteMark(metrics.authenticateEndTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.ACCESS_CHECK_START,
			getHostSiteMark(metrics.accessCheckStartTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.ACCESS_CHECK_END,
			getHostSiteMark(metrics.accessCheckEndTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.WIDGET_INIT,
			getHostSiteMark(metrics.instanceInitTime),
		);
		if (isBubbleImageLoadComplete(metrics)) {
			widgetLoadExperience.mark(
				WidgetLoadExperienceMarks.BUBBLE_IMAGE_LOAD_START,
				getHostSiteMark(metrics.bubbleImageLoadStartTime),
			);
			widgetLoadExperience.mark(
				WidgetLoadExperienceMarks.BUBBLE_IMAGE_LOAD_END,
				getHostSiteMark(metrics.bubbleImageLoadEndTime),
			);
		}
		/**
		 * FMP is when the launcher is first visible. A custom tile hides the bubble until
		 * the image loads, so prefer that end time. Hosts that predate the metric fall
		 * back to shell mount, then instance init. Defer FMP when the tile is still loading.
		 */
		if (!isBubbleImageLoadPending(metrics)) {
			widgetLoadExperience.markFMP(
				getHostSiteMark(
					metrics.bubbleImageLoadEndTime ?? metrics.shellMountTime ?? metrics.instanceInitTime,
				),
			);
		}
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_START,
			getHostSiteMark(metrics.iframeHTMLLoadStartTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.IFRAME_HTML_LOAD_END,
			getHostSiteMark(metrics.iframeHTMLLoadEndTime),
		);
		widgetLoadExperience.mark(WidgetLoadExperienceMarks.IFRAME_LOAD_END, metrics.iframeLoadEndTime);
	} catch {
		// UFO / host timestamps must not prevent the iframe from initialising.
	}
};

export const markBubbleImageLoadAndFMP = (metrics: {
	scriptLoadStartTime?: number;
	iframeInitTime: number;
	bubbleImageLoadStartTime: number;
	bubbleImageLoadEndTime: number;
}): void => {
	try {
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.BUBBLE_IMAGE_LOAD_START,
			getHostSiteMarkForMetrics(metrics, metrics.bubbleImageLoadStartTime),
		);
		widgetLoadExperience.mark(
			WidgetLoadExperienceMarks.BUBBLE_IMAGE_LOAD_END,
			getHostSiteMarkForMetrics(metrics, metrics.bubbleImageLoadEndTime),
		);
		widgetLoadExperience.markFMP(
			getHostSiteMarkForMetrics(metrics, metrics.bubbleImageLoadEndTime),
		);
	} catch {
		// UFO / host timestamps must not prevent the iframe from initialising.
	}
};

export const initUFOV1 = (): void => {
	payloadPublisher.setup({
		gasv3: client,
		product: 'csm',
		app: {
			version: { web: process.env.PACKAGE_VERSION ?? 'unknown' },
		},
	});
};
